<template>
  <div class="teaserWrapper">
    <Card v-if="loading" :loadingState="true" class="card-extra-border"></Card>
    <TextImageTeaser
      v-if="!loading && !showJsonTeaser && showTeaser"
      id="textTeaser"
      class="textTeaser"
      vertical-pic-position="center"
    >
      <template #text>
        <div class="teaser--centered">
          <p
            class="remove-margin"
            v-dompurify-html="$t('dashboardTeaser.teasertext')"
          ></p>
        </div>
      </template>
      <template #image>
        <img
          class="img"
          :src="`${assetsBaseUrl}/assets/images/Beraterbild353x186.png`"
          alt=""
        />
      </template>
    </TextImageTeaser>
    <TextImageTeaser
      v-if="!loading && showJsonTeaser && showTeaser && jsonTeaser"
      id="jsonTeaser"
      class="jsonTeaser"
      vertical-pic-position="center"
    >
      <template #text>
        <div>
          <h2 v-dompurify-html="jsonTeaser.content.headline" />
          <div class="center-mobile" v-dompurify-html="jsonTeaser.content.text" />
          <div class="center-mobile"></div>
          <div
            v-for="button in jsonTeaser.buttons"
            :key="button.label"
            class="button-spacing"
          >
            <CustomButton
              @click="() => (jsonTeaser ? redirect(button) : null)"
              :buttonColor="
                button.color.toLowerCase() === 'primary'
                  ? ButtonColor.PRIMARY
                  : button.color.toLowerCase() === 'secondary'
                    ? ButtonColor.SECONDARY
                    : button.color.toLowerCase() === 'tertiary'
                      ? ButtonColor.TERTIARY
                      : button.color.toLowerCase() === 'link'
                        ? ButtonColor.LINK
                        : ButtonColor.LINK_BIG
              "
              >{{ button.label }}</CustomButton
            >
          </div>
        </div>
      </template>
      <template #image>
        <img
          :src="`${jsonTeaser.image.url}`"
          :alt="`${jsonTeaser.image.alt}`"
          class="test-seal"
        />
      </template>
    </TextImageTeaser>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { ButtonColor, Card, CustomButton, TextImageTeaser } from 'de-zurich-ui-library'
import { isRedCooperationPartner } from '@/utilsTenant/RedCooperationUtils'
import type { Teaser, TeaserButton } from '@/types/rahmen/sitecore-teaser'
import { handleTeaserButtonNavigation } from '@/utils/NavigationUtils'

dayjs.extend(customParseFormat)

defineProps<{
  showModificationDate?: boolean
  isPlacedInHeader: boolean
}>()

useI18n()

const assetsBaseUrl = ref(globalThis.ENV_CONFIG?.ASSETS_BASE_URL.CUSTOMER_PORTAL || '')
const jsonFileName = ref('teaser1')
const loading = ref(true)
const showJsonTeaser = ref(false)
const isRedCooperationPartnerComputed = computed(() => isRedCooperationPartner() ?? true)
const jsonTeaser = ref<Teaser | undefined>()
const assetsUrl = globalThis.ENV_CONFIG?.SITECORE.ASSETS_URL || ''

onMounted(async () => {
  try {
    const response = await fetch(
      `${globalThis.ENV_CONFIG?.SITECORE.BASE_URL_JSS}${globalThis.ENV_CONFIG?.SITECORE.JSON_URL_JSS}?name=${jsonFileName.value}`
    )
    if (!response.ok) {
      showJsonTeaser.value = false
      loading.value = false
      console.error('Error in Request: Did not receive a valid json for teaser 1')
      return
    }
    const jsonTeaserValue = (await response.json()) as Teaser
    if (
      'image' in jsonTeaserValue &&
      'content' in jsonTeaserValue &&
      'buttons' in jsonTeaserValue &&
      'settings' in jsonTeaserValue
    ) {
      jsonTeaser.value = jsonTeaserValue
      showJsonTeaser.value = true
    }
    loading.value = false
  } catch {
    showJsonTeaser.value = false
    loading.value = false
    console.error('Error in Request: Did not receive a valid json for teaser 2')
  } finally {
    if (jsonTeaser.value?.settings?.hide) {
      showJsonTeaser.value = false
    }
    if (
      isRedCooperationPartnerComputed.value &&
      jsonTeaser.value?.settings?.isAdvertisement
    ) {
      showJsonTeaser.value = false
    }
  }
})

const showTeaser = computed(() => {
  return screen.width >= 600
})

const redirect = (button: TeaserButton) => {
  handleTeaserButtonNavigation(button.url, button.target)
}
</script>

<style lang="scss" scoped>
:deep(.card-extra-border) {
  border: 1px solid var(--cta2-border-color);
  border-radius: var(--border-radius-test-image-teaser);
}

.teaserWrapper {
  width: 100%;
  margin-bottom: var(--postbox-teaser-m-b);
}

:deep(.textTeaser) {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  .teaser {
    height: 100%;
    margin-bottom: 0;
    @media (max-width: 799px) {
      .teaser--centered {
        padding-top: 24px;
      }
      .teaser__pic {
        width: 75%;
      }
    }
    .teaser__content__top {
      margin: 0 !important;
    }
    .teaser--centered,
    .teaser__content__top {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
      .remove-margin {
        margin: 0 !important;
      }
    }
  }
}

:deep(.jsonTeaser) {
  .teaser.default {
    background-color: white !important;
    border: 1px solid var(--cta2-border-color) !important;
    margin-bottom: 0;
  }
  .teaser__content h2 {
    font-size: 22px;
    line-height: 28px;
  }
  @media (max-width: 799px) {
    .teaser__pic {
      max-width: 200px;
    }
  }
}

:deep() {
  .jsonTeaser {
    .button-spacing:not(:last-child) {
      margin-bottom: 16px;
    }

    .list-checks li,
    .center-mobile {
      font-size: 14px;
      line-height: 16px;
      margin-bottom: 14px;
    }

    ul.list-checks {
      margin-bottom: 18px;
    }
    .list-checks li {
      padding-left: 32px !important;
      width: 100%;
    }

    .list-checks li:before {
      font-size: 20px;
    }

    .card {
      background: var(--base-bg);
      border: 1px solid var(--primary-color-2) !important;
      border-radius: 20px;

      strong {
        font-size: 14px !important;
      }

      .card__content {
        padding: 24px 32px;
      }

      .card-row {
        gap: 0 !important;
      }

      .inline_left {
        display: inline;
      }

      .inline_right {
        align-items: center;
        display: inline-flex;

        img.test-seal {
          width: 105%;
          height: auto;
          padding-left: 32px;
          padding-top: 0px;
        }
      }

      .card__content--xs {
        text-align: center;

        .inline_left {
          text-align: start;
        }

        .inline_right {
          width: 80%;
          max-width: 220px;

          img.test-seal {
            width: 105%;
            height: auto;
            padding-left: 0px;
            padding-top: 32px;
          }
        }
      }

      .card__content--sm,
      .card__content--md,
      .card__content--lg {
        .inline_right {
          width: 180%;
          max-width: 250px;
          min-width: 220px;
        }
      }

      h2 {
        font-size: 22px;
        line-height: 28px;
      }
    }

    .flex-footer {
      margin-bottom: 12px;
      flex-wrap: unset;

      .flex-footer__left {
        width: 45%;
      }
    }

    .flex-footer--xs {
      width: 100% !important;
      flex-wrap: wrap !important;
      flex-direction: unset;
      margin-bottom: 0;

      .flex-footer__left {
        width: 100%;
        margin-bottom: 12px;
      }

      .flex-footer__right {
        width: 100%;
      }
    }
  }
}

@media (max-width: 600px) {
  .center-mobile {
    text-align: center;
  }
}
</style>
